import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const IntakeQuestionnaire = ({ clientId, data, onChange, onComplete }) => {
  const [formData, setFormData] = useState({
    occupation: '',
    lifestyleActivity: '',
    exerciseExperience: '',
    exerciseFrequency: '',
    preferredActivities: [],
    motivationLevel: 5,
    primaryMotivation: '',
    availableTime: '',
    workoutPreference: '',
    injuries: '',
    limitations: '',
    ...data
  });

  useEffect(() => {
    onChange(formData);
    checkCompletion();
  }, [formData]);

  const checkCompletion = () => {
    const required = ['occupation', 'lifestyleActivity', 'exerciseExperience', 'exerciseFrequency', 'primaryMotivation', 'availableTime', 'workoutPreference'];
    const isComplete = required?.every(field => formData?.[field]?.trim());
    onComplete(isComplete);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleActivityToggle = (activity) => {
    setFormData(prev => {
      const current = prev?.preferredActivities || [];
      const updated = current?.includes(activity)
        ? current?.filter(a => a !== activity)
        : [...current, activity];
      return { ...prev, preferredActivities: updated };
    });
  };

  const lifestyleOptions = [
    { value: 'sedentary', label: 'Sedentary', description: 'Desk job, minimal movement', icon: 'Coffee' },
    { value: 'light', label: 'Lightly Active', description: 'Some walking, light tasks', icon: 'Walk' },
    { value: 'moderate', label: 'Moderately Active', description: 'Regular movement, active job', icon: 'Activity' },
    { value: 'very-active', label: 'Very Active', description: 'Physical job, constant movement', icon: 'Zap' }
  ];

  const experienceLevels = [
    { value: 'beginner', label: 'Beginner', description: 'New to structured exercise' },
    { value: 'intermediate', label: 'Intermediate', description: '6-12 months experience' },
    { value: 'advanced', label: 'Advanced', description: '1+ years consistent training' }
  ];

  const activityTypes = [
    { value: 'strength', label: 'Strength Training', icon: 'Dumbbell' },
    { value: 'cardio', label: 'Cardio', icon: 'Heart' },
    { value: 'yoga', label: 'Yoga/Flexibility', icon: 'Wind' },
    { value: 'sports', label: 'Sports', icon: 'Trophy' },
    { value: 'hiit', label: 'HIIT', icon: 'Zap' },
    { value: 'outdoor', label: 'Outdoor Activities', icon: 'Mountain' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Intake Questionnaire</h2>
        <p className="text-muted-foreground">Tell us about your lifestyle and exercise background</p>
      </div>

      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Briefcase" size={20} className="text-primary" />
          Lifestyle Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Occupation"
            name="occupation"
            value={formData?.occupation}
            onChange={handleInputChange}
            placeholder="e.g., Software Engineer, Teacher"
            required
          />
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Available Time for Exercise
            </label>
            <select
              name="availableTime"
              value={formData?.availableTime}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">Select time availability...</option>
              <option value="30-min">30 minutes/day</option>
              <option value="45-min">45 minutes/day</option>
              <option value="60-min">1 hour/day</option>
              <option value="90-min">1.5 hours/day</option>
              <option value="flexible">Flexible schedule</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lifestyle Activity Level */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Daily Activity Level</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {lifestyleOptions?.map((option) => (
            <button
              key={option?.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, lifestyleActivity: option?.value }))}
              className={`
                p-4 rounded-xl border-2 transition-all text-left hover:scale-[1.02]
                ${
                  formData?.lifestyleActivity === option?.value
                    ? 'border-primary bg-primary/10 shadow-lg'
                    : 'border-border hover:border-primary/50 bg-background'
                }
              `}
            >
              <div className="flex items-center gap-3">
                <div className={`
                  w-12 h-12 rounded-lg flex items-center justify-center
                  ${formData?.lifestyleActivity === option?.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}
                `}>
                  <Icon name={option?.icon} size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{option?.label}</h4>
                  <p className="text-sm text-muted-foreground">{option?.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Exercise Experience */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Award" size={20} className="text-primary" />
          Exercise Experience
        </h3>
        <div className="space-y-3">
          {experienceLevels?.map((level) => (
            <button
              key={level?.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, exerciseExperience: level?.value }))}
              className={`
                w-full p-4 rounded-xl border-2 transition-all text-left hover:scale-[1.01]
                ${
                  formData?.exerciseExperience === level?.value
                    ? 'border-primary bg-primary/10 shadow-lg'
                    : 'border-border hover:border-primary/50 bg-background'
                }
              `}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-foreground">{level?.label}</h4>
                  <p className="text-sm text-muted-foreground">{level?.description}</p>
                </div>
                {formData?.exerciseExperience === level?.value && (
                  <Icon name="CheckCircle" size={24} className="text-primary" />
                )}
              </div>
            </button>
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Current Exercise Frequency
          </label>
          <select
            name="exerciseFrequency"
            value={formData?.exerciseFrequency}
            onChange={handleInputChange}
            className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
          >
            <option value="">Select frequency...</option>
            <option value="none">Not currently exercising</option>
            <option value="1-2">1-2 times per week</option>
            <option value="3-4">3-4 times per week</option>
            <option value="5-6">5-6 times per week</option>
            <option value="daily">Daily</option>
          </select>
        </div>
      </div>

      {/* Preferred Activities */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Preferred Activities</h3>
        <p className="text-sm text-muted-foreground">Select all that interest you</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {activityTypes?.map((activity) => (
            <button
              key={activity?.value}
              type="button"
              onClick={() => handleActivityToggle(activity?.value)}
              className={`
                p-4 rounded-xl border-2 transition-all hover:scale-105
                ${
                  formData?.preferredActivities?.includes(activity?.value)
                    ? 'border-primary bg-primary/10 shadow-lg'
                    : 'border-border hover:border-primary/50 bg-background'
                }
              `}
            >
              <div className="flex flex-col items-center gap-2">
                <div className={`
                  w-12 h-12 rounded-lg flex items-center justify-center
                  ${formData?.preferredActivities?.includes(activity?.value) ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}
                `}>
                  <Icon name={activity?.icon} size={24} />
                </div>
                <span className="text-sm font-medium text-foreground text-center">{activity?.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Motivation */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Target" size={20} className="text-primary" />
          Motivation & Preferences
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Primary Motivation
            </label>
            <select
              name="primaryMotivation"
              value={formData?.primaryMotivation}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">Select motivation...</option>
              <option value="weight-loss">Weight Loss</option>
              <option value="muscle-gain">Muscle Gain</option>
              <option value="health">General Health</option>
              <option value="performance">Athletic Performance</option>
              <option value="stress">Stress Management</option>
              <option value="energy">Increase Energy</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Workout Preference
            </label>
            <select
              name="workoutPreference"
              value={formData?.workoutPreference}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">Select preference...</option>
              <option value="gym">Gym-based</option>
              <option value="home">Home workouts</option>
              <option value="outdoor">Outdoor activities</option>
              <option value="hybrid">Hybrid (mix of all)</option>
            </select>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">Motivation Level</label>
            <span className="text-lg font-bold text-primary">{formData?.motivationLevel}/10</span>
          </div>
          <input
            type="range"
            name="motivationLevel"
            min="1"
            max="10"
            value={formData?.motivationLevel}
            onChange={handleInputChange}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Low</span>
            <span>High</span>
          </div>
        </div>
      </div>

      {/* Additional Notes */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Additional Information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Previous Injuries or Concerns
            </label>
            <textarea
              name="injuries"
              value={formData?.injuries}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
              rows={3}
              placeholder="Describe any previous injuries or areas of concern..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Physical Limitations
            </label>
            <textarea
              name="limitations"
              value={formData?.limitations}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
              rows={3}
              placeholder="Any physical limitations or restrictions..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntakeQuestionnaire;